'use client';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import getConfig from '../../config/aws-config';
import { getUserInfo } from '../../lib/auth';

const IS_BROWSER = typeof window !== 'undefined';

/** Node / misconfigured `--localstorage-file` can expose `localStorage` without working `getItem`. */
function browserLocalStorageUsable(): boolean {
  if (!IS_BROWSER) return false;
  try {
    const ls = window.localStorage;
    if (!ls || typeof ls.getItem !== 'function' || typeof ls.setItem !== 'function') return false;
    const k = `__nest_ls_${Math.random().toString(36).slice(2)}`;
    ls.setItem(k, '1');
    const ok = ls.getItem(k) === '1';
    ls.removeItem(k);
    return ok;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Amplify configuration
// ---------------------------------------------------------------------------

function getUserTypeFromUrl(): string {
  if (!IS_BROWSER) return 'customer';
  const { pathname } = window.location;
  let userType = '';
  try {
    if (browserLocalStorageUsable()) {
      userType = window.localStorage.getItem('ai-user-type') || '';
    }
  } catch {
    userType = '';
  }
  if (pathname === '/ai/supplier' || userType === 'supplier') return 'supplier';
  if (pathname === '/ai/designer' || userType === 'designer') return 'designer';
  return 'customer';
}

function configureAmplify(): void {
  if (!IS_BROWSER || !browserLocalStorageUsable()) return;
  const userType = getUserTypeFromUrl();
  try {
    window.localStorage.setItem('ai-user-type', userType);
  } catch {
    /* ignore */
  }
  const domain = window.location.hostname;
  const protocol = window.location.protocol.replace(':', '');
  try {
    const config = getConfig({ domain, protocol, userType: userType as 'customer' | 'designer' | 'supplier' });
    Amplify.configure(config);
  } catch (error) {
    console.warn('[nestingale_auth] Amplify configure error:', error);
  }
}

let amplifyConfigured = false;

function ensureAmplifyConfigured(): void {
  if (!IS_BROWSER || amplifyConfigured) return;
  if (!browserLocalStorageUsable()) {
    console.warn('[nestingale_auth] localStorage not usable; skipping Amplify.configure until it is.');
    return;
  }
  configureAmplify();
  amplifyConfigured = true;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LoginInfo {
  email?: string;
  name?: string;
  identityId?: string;
  idToken?: string;
  /** From custom:partner_type (product-supplier, interior-designer, customer) */
  loginProfile?: string;
  /** Raw value from custom:role attribute */
  role?: string;
  permissions?: unknown;
  customAttributes?: Record<string, unknown>;
  vendorId?: string;
}

export interface SessionInfo {
  session: LoginInfo | null;
  providerType: string;
  isSessionValid: boolean;
  userType?: string;
}

// ---------------------------------------------------------------------------
// In-memory session cache
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 4 * 60 * 1000; // 4 minutes (tokens last 5 min by default)

interface CacheEntry {
  promise: Promise<SessionInfo>;
  expiresAt: number;
}

let _sessionCache: CacheEntry | null = null;

/** Bust the in-memory cache — call on sign-out or auth events  */
export function invalidateSessionCache(): void {
  _sessionCache = null;
}

// ---------------------------------------------------------------------------
// Core session fetch (not exported — callers use getNestAwsSession)
// ---------------------------------------------------------------------------

async function _fetchSession(): Promise<SessionInfo> {
  const userType = getUserTypeFromUrl();

  try {
    // getUserInfo() internally calls getCurrentUser() + fetchUserAttributes()
    const userInfo = await getUserInfo();

    if (!userInfo) {
      return { session: null, providerType: '', isSessionValid: false, userType };
    }

    const authSession = await fetchAuthSession();
    const idToken = authSession.tokens?.idToken?.toString();

    const sessionData: LoginInfo = {
      email: userInfo.email,
      name: userInfo.name,
      identityId: userInfo.sub,
      idToken,
      loginProfile: userInfo.customAttributes?.partner_type as string | undefined || userType,
      role: userInfo.customAttributes?.role as string | undefined || userType,
      vendorId: userInfo.customAttributes?.vendorId as string | undefined,
      permissions: userInfo.customAttributes?.permissions,
      customAttributes: userInfo.customAttributes as Record<string, unknown>,
    };

    if (!sessionData.email || !sessionData.identityId) {
      console.warn('[nestingale_auth] Session found but email/identityId missing:', sessionData);
      return { session: sessionData, providerType: 'cognito', isSessionValid: false, userType };
    }

    return { session: sessionData, providerType: 'cognito', isSessionValid: true, userType };
  } catch (error) {
    const message = (error as Error)?.message ?? String(error);
    // These are normal "not logged in" errors — not worth warning about
    if (message !== 'The user is not authenticated' && message !== 'not authenticated') {
      console.warn('[nestingale_auth] Unexpected error fetching session:', error);
    }
    return { session: null, providerType: '', isSessionValid: false, userType };
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the current Cognito session. Results are cached for up to 4 minutes
 * so multiple callers (e.g. axios interceptors firing in parallel) share one
 * network round-trip.
 */
export async function getNestAwsSession(): Promise<SessionInfo> {
  if (!IS_BROWSER) {
    return { session: null, providerType: '', isSessionValid: false };
  }
  if (!browserLocalStorageUsable()) {
    return {
      session: null,
      providerType: '',
      isSessionValid: false,
      userType: getUserTypeFromUrl(),
    };
  }
  ensureAmplifyConfigured();

  const now = Date.now();
  if (_sessionCache && now < _sessionCache.expiresAt) {
    return _sessionCache.promise;
  }

  const promise = _fetchSession();
  _sessionCache = { promise, expiresAt: now + CACHE_TTL_MS };
  return promise;
}

export async function handleSignOut(): Promise<void> {
  if (!IS_BROWSER) return;

  const userType = getUserTypeFromUrl();
  try {
    await signOut({ global: true });

    invalidateSessionCache();
    if (browserLocalStorageUsable()) {
      try {
        window.localStorage.removeItem('ai-user-type');
        window.localStorage.removeItem('loginFrom');
        window.localStorage.removeItem('cogls');
      } catch {
        /* ignore */
      }
    }

    window.location.href = `/${userType === 'designer' ? 'interior-designer' : 'product-supplier'}`;
  } catch (error) {
    console.error('[nestingale_auth] Sign-out error:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Hub listener — auto-invalidate cache on auth events
// ---------------------------------------------------------------------------

if (IS_BROWSER) {
  Hub.listen('auth', ({ payload }) => {
    switch (payload.event) {
      case 'signedIn':
      case 'signedOut':
      case 'tokenRefresh':
      case 'tokenRefresh_failure':
        invalidateSessionCache();
        break;
    }
  });
}